// src/components/PanelDriver/DriverSlice/Driverslice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { generateGridCode } from "./Gridcode";
import {
  saveDriverProfile,
  saveDriverProfileForced,
  saveAvatarConfig,
  getDriverProfile,
  hasAcceptedConsent,
} from "./Drivercookie";

const API_URL = window.location.origin.includes("192.168")
  ? "http://192.168.1.101:5010/api/"
  : "http://localhost:5010/api/";

const SESSION_KEY = "vk_verify_token";

const DEFAULT_AVATAR = { face: "neutral", visor: "none", suit: "carbon", skin: "medium", number: "" };

function isAtLeast21(dobString) {
  if (!dobString) return true;
  const dob = new Date(dobString);
  const cutoff = new Date();
  cutoff.setFullYear(cutoff.getFullYear() - 21);
  return dob <= cutoff;
}

// ─── REGISTER ─────────────────────────────────────────────────────────────────
export const registerDriver = createAsyncThunk(
  "driver/register",
  async (formData, { rejectWithValue }) => {
    if (formData.birthday && !isAtLeast21(formData.birthday)) {
      return rejectWithValue("Driver must be at least 21 years old.");
    }
    try {
      // Generate grid code client-side
      const gridCode = generateGridCode(formData.email);

      // FIX 1: Send gridCode as serialNumber to backend so MongoDB stores it
      const res = await axios.post(`${API_URL}register`, {
        ...formData,
        serialNumber: gridCode,
      });
      if (!res.data.status) return rejectWithValue(res.data.message);

      // FIX 2: Save gridCode to cookie so returning users keep their code
      if (hasAcceptedConsent()) {
        saveDriverProfile({
          registered: false,
          callsign: formData.callsign || "",
          email: formData.email,
          gridCode,                              // ← now saved to cookie
          registeredAt: new Date().toISOString(),
        });
      }

      return { ...res.data, gridCode };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Network error — check your connection");
    }
  }
);

// ─── VERIFY EMAIL ─────────────────────────────────────────────────────────────
export const verifyDriverEmail = createAsyncThunk(
  "driver/verifyEmail",
  async ({ token, driverId } = {}, { rejectWithValue }) => {
    try {
      if (token) sessionStorage.setItem(SESSION_KEY, token);
      const resolvedToken = token || sessionStorage.getItem(SESSION_KEY);
      if (!resolvedToken) return rejectWithValue("No verification token found.");

      const res = await axios.post(`${API_URL}verify`, {
        token: resolvedToken,
        ...(driverId ? { driverId } : {}),
      });
      if (!res.data.status) return rejectWithValue(res.data.message);

      const existing = getDriverProfile() || {};

      // Preserve the gridCode that was saved during registration
      // Fallback: use serialNumber from DB response if cookie was cleared
      const finalGridCode =
        existing.gridCode ||
        res.data.driver?.serialNumber ||
        null;

      saveDriverProfileForced({
        ...existing,
        registered: true,
        callsign: res.data.driver?.callsign || existing.callsign || "",
        gridCode: finalGridCode,               // ← preserve gridCode on verify
        verifiedAt: new Date().toISOString(),
      });

      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Verification failed — please try again.");
    }
  }
);

// ─── UPDATE AVATAR ────────────────────────────────────────────────────────────
export const updateAvatar = createAsyncThunk(
  "driver/updateAvatar",
  async (avatarConfig, { rejectWithValue }) => {
    try {
      saveAvatarConfig(avatarConfig);
      return avatarConfig;
    } catch {
      return rejectWithValue("Could not save avatar.");
    }
  }
);

// ─── INITIAL STATE ────────────────────────────────────────────────────────────
const cookieProfile = getDriverProfile();

const initialState = {
  // Form fields
  name: "",
  callsign: "",
  email: "",
  birthday: "",
  driverClass: "",
  termsChecked: false,
  newsletterOptIn: false,

  // Avatar — always starts at default, restored from cookie only for returning verified users
  avatar: DEFAULT_AVATAR,

  // Registration flow
  status: "idle",
  error: null,
  registeredDriver: null,
  gridCode: null,   // set after fresh registerDriver success; cleared on resetForm

  // Email verify
  verifyStatus: "idle",
  verifyError: null,
  verifiedDriver: null,

  // Cookie-derived state (for returning verified users)
  cookieRegistered: cookieProfile?.registered === true,
  cookieCallsign:   cookieProfile?.callsign || "",
  // NOTE: cookieGridCode is in cookie but we intentionally do NOT surface it
  // in the driver panel card for returning users — only shown right after fresh register.
  // It IS saved to cookie so the VerifiedPage and future features can use it.
  cookieAvatar:     cookieProfile?.avatar || null,
};

// ─── SLICE ────────────────────────────────────────────────────────────────────
const driverSlice = createSlice({
  name: "driver",
  initialState,

  reducers: {
    setField(state, action) {
      const { field, value } = action.payload;
      state[field] = value;
      if (field !== "error") state.error = null;
    },
    setDriverClass(state, action) {
      state.driverClass = state.driverClass === action.payload ? "" : action.payload;
    },
    resetForm(state) {
      return {
        ...initialState,
        // Preserve cookie state for verified returning user
        cookieRegistered: state.cookieRegistered,
        cookieCallsign:   state.cookieCallsign,
        cookieAvatar:     state.cookieAvatar,
        // Always reset form + avatar + gridCode
        avatar:   DEFAULT_AVATAR,
        gridCode: null,
      };
    },
    clearError(state) {
      state.error = null;
    },
    refreshCookieState(state) {
      const p = getDriverProfile();
      state.cookieRegistered = p?.registered === true;
      state.cookieCallsign   = p?.callsign || "";
      state.cookieAvatar     = p?.avatar || null;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(registerDriver.pending,   (s) => { s.status = "loading"; s.error = null; })
      .addCase(registerDriver.fulfilled, (s, a) => {
        s.status           = "succeeded";
        s.registeredDriver = a.payload.driver;
        s.gridCode         = a.payload.gridCode;   // in-memory for success screen
      })
      .addCase(registerDriver.rejected,  (s, a) => { s.status = "failed"; s.error = a.payload; });

    builder
      .addCase(verifyDriverEmail.pending,   (s) => { s.verifyStatus = "loading"; s.verifyError = null; })
      .addCase(verifyDriverEmail.fulfilled, (s, a) => {
        s.verifyStatus     = "succeeded";
        s.verifiedDriver   = a.payload.driver;
        s.cookieRegistered = true;
        s.cookieCallsign   = a.payload.driver?.callsign || s.cookieCallsign;
      })
      .addCase(verifyDriverEmail.rejected, (s, a) => { s.verifyStatus = "failed"; s.verifyError = a.payload; });

    builder
      .addCase(updateAvatar.fulfilled, (s, a) => { s.avatar = a.payload; });
  },
});

export const { setField, setDriverClass, resetForm, clearError, refreshCookieState } = driverSlice.actions;

export const selectDriverForm       = (s) => s.driver;
export const selectDriverStatus     = (s) => s.driver.status;
export const selectDriverError      = (s) => s.driver.error;
export const selectRegisteredDriver = (s) => s.driver.registeredDriver;
export const selectGridCode         = (s) => s.driver.gridCode;
export const selectAvatar           = (s) => s.driver.avatar;
export const selectVerifyStatus     = (s) => s.driver.verifyStatus;
export const selectVerifyError      = (s) => s.driver.verifyError;
export const selectVerifiedDriver   = (s) => s.driver.verifiedDriver;
export const selectCookieRegistered = (s) => s.driver.cookieRegistered;
export const selectCookieCallsign   = (s) => s.driver.cookieCallsign;
export const selectCookieAvatar     = (s) => s.driver.cookieAvatar;

export default driverSlice.reducer;